import * as THREE from 'three';
import type { Chapter, ChapterContext, ScreenLabel } from './types';
import { LETTER } from '../data/letter';
import { GalaxyWorld } from '../render/galaxy/world';
import { HALO_MIN, HALO_MAX, SUN_SPEED_KMS, sunSpeedKms, sunAngularRate, invisibleMassRatio, withinMeasured, haloForSpeed } from '../render/galaxy/potential';
import { DOLLY_SECONDS, ORBIT_TARGET, clamp, clampElevation, clampRadius, dollyPose, fromSpherical, toSpherical } from '../render/galaxy/camera';

const meta = LETTER.chapters.find((c) => c.id === 'wider')!;

/** the galaxy's well slows a clock by about two parts in a million */
const RATE = 1 - 1.7e-6;
/** the chapter starts with far too little invisible mass: the Sun crawls */
const HALO_START = HALO_MIN;
/** how long the speed has to stay in the window */
const HOLD_SECONDS = 1.5;
/** Show me slides the slider to the answer over this long */
const DEMO_SECONDS = 3;
/** a snap to the exact answer bigger than this, in km/s, is worth telling the reader about */
const SNAP_NOTICE_KMS = 0.5;
/** at most 20 lattice rebuilds a second while the slider moves */
const REBUILD_INTERVAL = 1 / 20;
const SUN_START_ANGLE = -1.9;
const IDLE_BEFORE_DRIFT = 1.5;
const DRIFT_RAD_PER_S = 0.03;
const FOV_DEG = 42;
const BACKGROUND = 0x03050b;

const reducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Chapter two: the Milky Way from above, the bowl of slowed time under it, and the weight that is missing.
 * The lever adds invisible mass until the Sun's orbit matches the speed Gaia measured.
 */
export class GalaxyChapter implements Chapter {
  readonly id = 'wider';
  readonly title = 'Wider';
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV_DEG, 16 / 9, 0.5, 3000);
  readonly description = 'The Milky Way seen from above, with a bowl of slowed time drawn under it; a slider adds invisible mass until the Sun orbits at the measured speed.';

  private world: GalaxyWorld | null = null;
  private ctx: ChapterContext | null = null;
  private active = false;
  private done = false;
  private hk = HALO_START;
  private latticeDirty = true;
  private sinceRebuild = Infinity;
  private holdT = 0;
  private sunAngle = SUN_START_ANGLE;
  private lastReadout = '';
  private reduceMotion = false;
  // Show me: the slider is moved from code, from where it is to the answer
  private demoActive = false;
  private demoT = 0;
  private demoFrom = HALO_START;
  private demoTo = HALO_START;
  // the Sun's label, reused every frame
  private readonly sunV = new THREE.Vector3();
  private readonly sunLabel: ScreenLabel = { text: 'The Sun', x: 0, y: 0 };
  private readonly labelList: ScreenLabel[] = [];
  // camera: the dolly's pose, plus whatever the viewer has added with the mouse
  private dollyT = 0;
  private idle = 0;
  private dAz = 0;
  private dEl = 0;
  private zoom = 1;
  private viewHeight = 1080;
  private readonly target = new THREE.Vector3(ORBIT_TARGET[0], ORBIT_TARGET[1], ORBIT_TARGET[2]);

  constructor() {
    this.scene.background = new THREE.Color(BACKGROUND);
    this.camera.up.set(0, 0, 1);
    this.pose();
  }

  /** The point cloud is built on first use, so nothing is generated until the voyage reaches this chapter. */
  private ensureBuilt(): GalaxyWorld {
    if (!this.world) this.world = new GalaxyWorld(this.scene);
    return this.world;
  }

  enter(ctx: ChapterContext) {
    this.ctx = ctx;
    this.active = true;
    const world = this.ensureBuilt();
    this.done = false;
    this.hk = HALO_START;
    this.demoActive = false;
    this.holdT = 0;
    this.sunAngle = SUN_START_ANGLE;
    this.lastReadout = '';
    this.reduceMotion = reducedMotion();
    this.dollyT = 0;
    this.idle = 0;
    this.dAz = 0;
    this.dEl = 0;
    this.zoom = 1;
    this.pose();
    world.setHalo(this.hk);
    world.setSunAngle(this.sunAngle);
    this.latticeDirty = false;
    this.sinceRebuild = 0;

    ctx.say(meta.intro);
    ctx.hud.setLever({
      kind: 'slider',
      label: 'Dark halo',
      min: HALO_MIN,
      max: HALO_MAX,
      step: 0.01,
      value: this.hk,
      format: (v: number) => Math.round(sunSpeedKms(v)) + ' km/s',
      onInput: (v: number) => {
        this.demoActive = false; // the reader has taken the slider back
        this.hk = v;
        this.latticeDirty = true;
        this.pushReadouts();
      }
    });
    ctx.hud.setMove(meta.lever);
    ctx.hud.setHint('');
    this.pushReadouts();
  }

  update(dt: number, _t: number, width: number, height: number) {
    const world = this.ensureBuilt();
    const step = Math.min(Math.max(dt, 0) || 0, 0.1);
    this.fitView(width, height);

    this.dollyT += step;
    this.idle += step;
    if (this.dollyT >= DOLLY_SECONDS && this.idle > IDLE_BEFORE_DRIFT && !this.reduceMotion) this.dAz += DRIFT_RAD_PER_S * step;
    this.pose();
    world.setViewScale(this.viewHeight, this.distance);

    if (this.demoActive) this.stepDemo(step);

    // the Sun's marker runs at the speed the current halo allows
    this.sunAngle += step * sunAngularRate(this.hk);
    world.setSunAngle(this.sunAngle);

    // the lattice follows the slider, at most 20 times a second
    this.sinceRebuild += step;
    if (this.latticeDirty && this.sinceRebuild >= REBUILD_INTERVAL) {
      world.setHalo(this.hk);
      this.latticeDirty = false;
      this.sinceRebuild = 0;
    }

    const ctx = this.ctx;
    if (!this.active || !ctx) return;

    if (!this.done) {
      if (withinMeasured(Math.round(sunSpeedKms(this.hk)))) this.holdT += step; else this.holdT = 0;
      if (this.holdT >= HOLD_SECONDS) this.succeed(ctx);
    }
    this.pushReadouts();

    ctx.clock.advance(dt * 3600, dt * 3600 * RATE);
    ctx.audio.setScene({ rate: RATE, dist: 60, phase: 'wider', orbitPhase: 0 });
  }

  exit() {
    this.active = false;
    this.demoActive = false;
    this.ctx?.hud.setLever(null);
    this.ctx?.hud.setMove(null);
    this.ctx?.hud.setHint('');
  }

  onDrag(dx: number, dy: number) {
    this.dAz -= dx * 0.006;
    this.dEl += dy * 0.005;
    this.idle = 0;
  }

  onWheel(deltaY: number) {
    this.zoom *= clamp(1 + deltaY * 0.001, 0.5, 2);
    this.idle = 0;
  }

  onClick(_x: number, _y: number) { /* nothing to aim at */ }

  /** The accessible route ("Show me"): slide the slider to the answer. The usual success test does the rest. */
  demonstrate() {
    if (!this.ctx || !this.active || this.done) return;
    this.demoFrom = this.hk;
    this.demoTo = haloForSpeed(SUN_SPEED_KMS, HALO_MIN, HALO_MAX);
    this.demoT = 0;
    this.demoActive = true;
  }

  /** The Sun, named where its marker is. Projected with the chapter camera. */
  labels(width: number, height: number): ScreenLabel[] {
    const out = this.labelList;
    out.length = 0;
    if (!this.world) return out;
    this.camera.updateMatrixWorld();
    const v = this.sunV.copy(this.world.sun.position).project(this.camera);
    if (v.z > 1) return out; // behind the camera
    this.sunLabel.x = ((v.x + 1) / 2) * width;
    this.sunLabel.y = ((1 - v.y) / 2) * height;
    out.push(this.sunLabel);
    return out;
  }

  /** Free the GPU buffers. The director does not need to call this, but it may. */
  dispose() {
    this.world?.dispose();
    this.world = null;
  }

  private succeed(ctx: ChapterContext) {
    this.done = true;
    this.demoActive = false;
    // The window is a few km/s wide. Land the slider and the readout exactly on Gaia's number, so the screen never
    // says 222 km/s beside "measured 230" while the chapter calls itself done.
    const off = Math.abs(sunSpeedKms(this.hk) - SUN_SPEED_KMS);
    this.hk = haloForSpeed(SUN_SPEED_KMS, HALO_MIN, HALO_MAX);
    this.latticeDirty = true;
    ctx.hud.setLeverValue(this.hk);
    this.pushReadouts();
    ctx.say(meta.payoff);
    ctx.measure('wider', LETTER.measurements.wider);
    ctx.audio.blip('ok');
    ctx.hud.setLever(null);
    ctx.hud.setMove(null);
    ctx.hud.setHint(off > SNAP_NOTICE_KMS ? `Close enough. Locked in at Gaia’s ${SUN_SPEED_KMS} km/s.` : 'Drag to look around. Scroll to zoom.');
    ctx.finish();
  }

  /** One frame of Show me: ease the slider from where it was to the answer, and keep the screen in step with it. */
  private stepDemo(dt: number) {
    const ctx = this.ctx;
    if (!ctx || !this.active || this.done) { this.demoActive = false; return; }
    this.demoT += dt;
    const k = Math.min(1, this.demoT / DEMO_SECONDS);
    const eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
    this.hk = k >= 1 ? this.demoTo : this.demoFrom + (this.demoTo - this.demoFrom) * eased;
    if (k >= 1) this.demoActive = false;
    this.latticeDirty = true;
    ctx.hud.setLeverValue(this.hk);
    this.pushReadouts();
  }

  private pushReadouts() {
    const ctx = this.ctx;
    if (!ctx || !this.active) return;
    const pairs: [string, string][] = [
      ['Orbit speed at the Sun', `${Math.round(sunSpeedKms(this.hk))} km/s`],
      ['Gaia measured', `${SUN_SPEED_KMS} km/s`],
      ['Invisible mass', `${invisibleMassRatio(this.hk).toFixed(1)}× the visible`]
    ];
    const key = pairs.map((p) => p[1]).join('|');
    if (key === this.lastReadout) return;
    this.lastReadout = key;
    ctx.hud.setReadouts(pairs);
  }

  private get distance() {
    return this.camera.position.distanceTo(this.target);
  }

  private fitView(width: number, height: number) {
    if (!(width > 0 && height > 0)) return;
    this.viewHeight = height;
    const aspect = width / height;
    // keep the same horizontal sweep on a portrait screen
    const fov = aspect >= 1 ? FOV_DEG : THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(FOV_DEG / 2)) / Math.max(aspect, 0.5)));
    if (Math.abs(aspect - this.camera.aspect) > 1e-4 || Math.abs(fov - this.camera.fov) > 1e-3) {
      this.camera.aspect = aspect;
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
      const lift = height > width * 1.05 ? 0.12 : 0;   // a phone held upright: the galaxy rides above the captions
      if (lift) this.camera.setViewOffset(width, height, 0, lift * height, width, height); else this.camera.clearViewOffset();
    }
  }

  /**
   * The entry dolly gives a base pose; its azimuth, elevation and radius about the orbit target are what the viewer's
   * drags and wheel add to. Once the dolly ends the base stops moving, so this is just an orbit camera.
   */
  private pose() {
    const base = dollyPose(this.dollyT);
    const sph = toSpherical(base.pos, ORBIT_TARGET);
    const el = clampElevation(sph.el + this.dEl);
    this.dEl = el - sph.el; // no wind-up past the clamps
    const radius = clampRadius(sph.radius * this.zoom);
    this.zoom = radius / sph.radius;
    const p = fromSpherical(ORBIT_TARGET, sph.az + this.dAz, el, radius);
    this.camera.position.set(p[0], p[1], p[2]);
    this.camera.lookAt(base.look[0], base.look[1], base.look[2]);
  }
}
